/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE REVIEW SHEET — one finished workout, set by set, beside what was written (W82 §2, the pro web step 3). PURE;
 * `reviewSheet.test.ts` runs it. The page (`routes/WorkoutReview.tsx`) draws what this returns and derives nothing.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Every rule below is a READ of a reader that already exists; this file adds the set-by-set comparison and its words:
 *
 *   • THE RECORD — the queue's own choice (`QueueRow.sessionId`: the day's fullest record by `oneReadingPerDay`). The day's
 *     other records (a restart, a second open — his 23 Sep holds three) are COUNTED, never read: summing records is the
 *     76 ms trap (the codex: 261 sets summed where 115 were performed).
 *   • THE HEAD'S NUMBERS — the queue row's (done · written · skipped · not logged · every flag by `flagsOf`, said by
 *     `flagWords`), so the review and the Dashboard can never disagree about the day they both describe.
 *   • THE PAIRING — `training.dayView` (plan flattened by section then exercise position, the session by position, the
 *     SAME library id at every place, or no pairing at all — never a partial one).
 *   • WORKING vs RAMP — `progressHistory.isWorking` on the log side; a written set typed `warm_up` is a ramp (the builder's
 *     `SET_TYPES`, the phone's `Unit.isRampSet`), shown greyed and counted nowhere.
 *   • THE RULE'S SENTENCE — `builderHead.ruleTag` (W73: `Rule · …`, the lacks apart), read off the LIBRARY row's
 *     `progression_config`, which is where the rule lives (`CoachProgression`: the copy's row carries none).
 *   • SUPERSETS — `builder.supersetRuns` (a flag on a section's last row joins nothing).
 *   • THE SET GRAMMAR — `phoneDose.kg` / `durationLabel` (the phone's own words for a load and a hold).
 *
 * ⚠️ A SET IS CLASSIFIED BY THE ONE MEASURE ITS ROW IS ABOUT: a HOLD (a written time) by seconds; a WEIGHTED row (a
 * written load above zero) by the load, then the reps; a BODYWEIGHT row by the reps alone. A measure the athlete did not
 * record claims nothing — the set reads `equal`, in ink, never short.
 */

import { isPerformed, isWorking } from "./progressHistory";
import { completionWords, flagWords, finishedWords, type QueueRow } from "./reviewQueue";
import {
  classify, flatPlan, isFoldedSection, isRampSet, isTyped, logsByNumber, pairWithPlan, sortedSets,
  type PlanExercise, type PlanSection, type PlanSet,
} from "./setVerdict";
import { ruleTag } from "./builderHead";
import { sectionName, supersetRuns } from "./builder";
import { durationLabel, kg, runPhrase } from "./phoneDose";
import { timeText } from "./typedCell";

/** The RPE column on the coach's review — LEFT OUT for now (his word 2026-10-07 evening, QUOTED: "idk if rpe is of any use
 *  for the coaches so leave that out for now"). The sheet still reads RPE (`rpeLogged`); only the coach's table stops
 *  drawing it. The phone's twin is `ReviewSheetRule.coachSeesRPE`. */
export const COACH_SEES_RPE = false;

/* ── the raw shapes the page's reads hand in ────────────────────────────────────────────────────── */

export type SheetLog = {
  readonly set_number: number | null;
  readonly status: string;
  readonly reps_completed: number | null;
  readonly weight_kg: number | null;
  readonly hold_seconds: number | null;
  readonly rpe: number | null;
  readonly note: string | null;
  /** W136 B2 S3 — `set_logs.distance_m` (metres) / `rounds`; absent = not read (an older fixture), NULL = not recorded. */
  readonly distance_m?: number | null;
  readonly rounds?: number | null;
};

export type SheetClip = {
  readonly id: string;
  readonly set_number: number | null;
  readonly media_kind: string;
  readonly storage_path: string;
  readonly recorded_at: string | null;
};

export type SheetSessionExercise = {
  readonly id: string;
  readonly position: number | null;
  readonly section_role: string | null;
  /** The library id — `coach_exercise_id ?? exercise_id`, as the queue keys it. */
  readonly key: string | null;
  /** The raw `coach_exercise_id` (W82 step 4): a comment names it, and the trigger holds it equal to this row's. Absent
   *  on a fixture that predates the step, which reads as "no coach exercise" — the row then takes no comment. */
  readonly coachExerciseId?: string | null;
  readonly logs: readonly SheetLog[];
  readonly clips: readonly SheetClip[];
};

export type SheetSession = {
  readonly id: string;
  readonly userId: string;
  readonly kind: string;
  readonly scheduledDate: string | null;
  /** `duration_actual_minutes`. */
  readonly minutes: number | null;
  /** `performance_notes` — the phone's workout note (`LocalWorkoutStore`: `workoutNote = row.performanceNotes`). */
  readonly note: string | null;
  readonly exercises: readonly SheetSessionExercise[];
};

/** The copy's shape — `setVerdict.ts`'s, the one home both the queue and the sheet read. */
export type { PlanExercise, PlanSection, PlanSet } from "./setVerdict";

/** The day's ONE training assignment's copy, or null when the day holds 0 or 2+ (the queue's `written` is then null too). */
export type SheetPlan = { readonly workoutName: string | null; readonly sections: readonly PlanSection[] };

/* ── what the page draws ────────────────────────────────────────────────────────────────────────── */

/** equal (ink) · short (the accent) · beaten (`--ok`) · not logged (secondary, an en dash) · skipped (secondary, a word).
 *  `ramp` is a written warm-up set: greyed, counted nowhere. */
export type SetState = "equal" | "short" | "beaten" | "notLogged" | "skipped" | "ramp";

export type SheetSetRow = {
  /** `1`, `2` … for working sets, `W1`, `W2` … for ramps — the working sets are numbered as the coach wrote them. */
  readonly label: string;
  readonly written: string;
  readonly done: string;
  readonly rpe: string;
  readonly state: SetState;
  readonly note: string | null;
  /** A run / rounds row (the written set or its log carries a distance or rounds) — its Done names only what moved
   *  (`movedParts`). `ReviewSheetRule.SetRow.typed`'s twin; not a corpus fact (the corpus test leaves it out). */
  readonly typed: boolean;
};

export type SheetExercise = {
  readonly id: string;
  readonly name: string;
  /** This week's copy row (`coach_workout_exercises.id`) — the key `reviewWrite.nextWeekCells` files its next-week cells
   *  under. Null when the record is shown unpaired (no plan to set next week against) or the plan was read without ids. */
  readonly planRowId: string | null;
  /** The library exercise (the plan's `exercise_id`, else the record's key) — what `held (set 4 short)` is keyed on. */
  readonly libraryId: string | null;
  /** Where a coach's ONE comment on this exercise goes: the performed row and its coach exercise. Null when there is no
   *  performed row or it carries no `coach_exercise_id` (the migration's trigger requires both). */
  readonly commentTarget: { readonly exerciseRowId: string; readonly coachExerciseId: string } | null;
  /** `Rule · …` (W73) with its lacks apart; null = no rule. */
  readonly rule: { readonly text: string; readonly lacks: string | null } | null;
  readonly sets: readonly SheetSetRow[];
  /** ⚠️ `label` is the TABLE's name for the clip's set (`Set 2`, `Warm-up 1`), never `set_number`: the phone numbers every
   *  row, ramps included, so set_number 3 is the table's `1` on a lift with two ramps (RENDERED: a clip on a ramp read
   *  `Set 1` beside a row the table called `W1`). */
  readonly clips: readonly { id: string; storagePath: string; mediaKind: string; recordedAtISO: string | null; setNumber: number | null; label: string }[];
};

export type SheetItem =
  | { readonly kind: "single"; readonly exercise: SheetExercise }
  | { readonly kind: "superset"; readonly exercises: readonly SheetExercise[] };

export type SheetBand =
  /** A warm-up or cool-down section: one caption line, its exercises folded. */
  | { readonly kind: "folded"; readonly line: string }
  | { readonly kind: "section"; readonly title: string | null; readonly items: readonly SheetItem[] };

export type Chip = { readonly text: string; readonly tone: "ok" | "warn" | "plain" };

export type ReviewSheet = {
  readonly head: {
    readonly client: string;
    readonly workout: string | null;
    /** `finished Wed 23 Sep 13:07` — the queue's date grammar. */
    readonly finished: string;
    readonly minutes: string | null;
    /** Line two, under the client's name alone: `Legs & Core · finished Wed 23 Sep 16:07 · 133 min` (Fable, 2026-09-26: his
     *  workout names carry the middle dot, so `Name · Workout` in one title hid the seam — step 2's lesson). */
    readonly line: string;
    readonly chips: readonly Chip[];
  };
  /** The same four counts the queue row carries — equal to it by construction, and ruled so. */
  readonly counts: { readonly done: number; readonly written: number | null; readonly skipped: number; readonly notLogged: number | null };
  /** What the table's own rows add up to (done = equal + short + beaten); the ruler holds it to `counts`. */
  readonly tally: { readonly done: number; readonly short: number; readonly skipped: number; readonly notLogged: number; readonly ramps: number };
  /** Whether ANY set of this workout carries an RPE — when none does, the RPE column is not drawn and the card says
   *  `no RPE logged` once (Fable: an empty column on every row of every still). */
  readonly rpeLogged: boolean;
  readonly bands: readonly SheetBand[];
  /** Why the rows could not be set beside the plan, when they could not; null when they were. */
  readonly unpaired: string | null;
  readonly workoutNote: string | null;
  /** The day's other records of this workout (restarts), which are NOT read. */
  readonly otherRecords: number;
  readonly otherRecordsLine: string | null;
};

/* ── the words ──────────────────────────────────────────────────────────────────────────────────── */

/** The en dash a not-logged cell draws. */
export const NOT_LOGGED = "–";

const plural = (n: number, one: string, many = `${one}s`) => `${String(n)} ${n === 1 ? one : many}`;

function rpeText(rpe: number | null): string {
  if (rpe === null) return "";
  return Number.isInteger(rpe) ? String(rpe) : rpe.toFixed(1);
}

/** One set in the phone's words: `5 @ 60 kg` · `22s @ 10 kg` · `8` · `30s`. A load of zero is not a load.
 *  W136 B2 S3 — a set carrying a distance or rounds is said by `runPhrase` (§5): `400 m in 1:45` · `9 km in 1:45:30 @ 20 kg` ·
 *  `7 rounds + 5 reps`; the unit is the coach FIELD's (`km`). `typed` — the WRITTEN set is a run: a log that recorded only its
 *  time says it typed (`0:30`), never as a hold (`ReviewSheetRule.setWords`). */
export function setWords(reps: number | null, seconds: number | null, weight: number | null,
                         distanceM: number | null = null, rounds: number | null = null, km = false, typed = false): string {
  const head = distanceM !== null || rounds !== null
    ? runPhrase(1, distanceM === null ? [] : [distanceM], rounds === null ? [] : [rounds],
                rounds !== null && reps !== null ? [reps] : [], seconds === null ? [] : [seconds], km)
    : typed ? (seconds === null ? null : timeText(seconds))
    : seconds !== null ? durationLabel(seconds) : reps !== null ? String(reps) : null;
  const load = weight !== null && weight > 0 ? `${kg(weight)} kg` : null;
  if (head === null) return load ?? NOT_LOGGED;
  return load === null ? head : `${head} @ ${load}`;
}

/** The counts, when the day earns neither `as written` nor `all sets done` (`completionWords`): `15 of 32 sets`, `15 sets`. */
export function setsChip(done: number, written: number | null): Chip {
  if (written === null) return { text: plural(done, "set"), tone: "plain" };
  return { text: `${String(done)} of ${plural(written, "set")}`, tone: "plain" };
}

/**
 * `The app was restarted twice that day · the fullest record is shown`; null when the day holds one record. The day's
 * other records ARE restarts (the phone opens a fresh record each time the workout is re-entered — measured on his 23 Sep,
 * confirmed by the coaching session), so a coach is told what happened, not how the database files it (Fable, 2026-09-26).
 */
export function otherRecordsWords(n: number): string | null {
  if (n === 0) return null;
  const times = n === 1 ? "once" : n === 2 ? "twice" : `${String(n)} times`;
  return `The app was restarted ${times} that day · the fullest record is shown`;
}

/** A folded section's one line: `Warm-up · 4 exercises · 8 of 8 sets logged` · `Cooldown · 2 exercises · nothing logged`. */
export function foldedLine(title: string | null, exercises: number, written: number, logged: number): string {
  // W104 — an unnamed folded section says its count only, never a generated name.
  const tally = `${plural(exercises, "exercise")} · ${logged === 0 ? "nothing logged" : `${String(logged)} of ${plural(written, "set")} logged`}`;
  return title === null ? tally : `${title} · ${tally}`;
}

/* ── the sheet ──────────────────────────────────────────────────────────────────────────────────── */

/** A logged set in its own measures (a run's distance and time, a rounds set's rounds) — every other log as before. */
/** A log measured in distance or rounds (`ReviewSheetRule.isTypedLog`). */
const typedLog = (l: SheetLog) => (l.distance_m ?? null) !== null || (l.rounds ?? null) !== null;

/** 🔴 A RUN'S DONE AND NEXT WEEK NAME ONLY WHAT MOVED (his word 2026-10-07: "no row should be 2 lines, none" — MEASURED in
 *  SF, three whole `9 km in 1:45:30 @ 20 kg` need 0.47 of a 375-pt row). `phrase` beside its Written, part by part (a part
 *  opens at `in`, `@` or `+`): the parts that differ (`in 1:46:40`, `10 km`, `@ 28 kg`), `same` when none does; the phrase
 *  whole when the two are not the same parts (`400 m` beside `400 m in 1:45`, `–`). `ReviewSheetRule.movedParts`' twin —
 *  both answer `tools/review-sheet-corpus/moved-parts.json`. */
export function movedParts(phrase: string, written: string): string {
  const parts = (s: string) => s.split(" ").filter((w) => w !== "").reduce<string[]>((out, w) => {
    if (out.length === 0 || MOVED_OPENERS.includes(w)) out.push(w); else out[out.length - 1] += ` ${w}`;
    return out;
  }, []);
  const kind = (part: string) => MOVED_OPENERS.find((o) => part.startsWith(`${o} `)) ?? "";
  const mine = parts(phrase), theirs = parts(written);
  if (mine.length !== theirs.length || mine.some((p, i) => kind(p) !== kind(theirs[i] ?? ""))) return phrase;
  const moved = mine.filter((p, i) => p !== theirs[i]);
  return moved.length === 0 ? MOVED_NONE : moved.join(" ");
}
const MOVED_OPENERS = ["in", "@", "+"];
/** `reviewWrite`'s `FROZEN_CELL` (an import would be a cycle; vitest pins the two equal). */
export const MOVED_NONE = "same";

const logWords = (l: SheetLog, km: boolean, typed = false) =>
  setWords(l.reps_completed, l.hold_seconds, l.weight_kg, l.distance_m ?? null, l.rounds ?? null, km, typed);

const noteOf = (l: SheetLog | undefined) => (l?.note !== undefined && l.note !== null && l.note.trim() !== "" ? l.note.trim() : null);

function clipsOf(e: SheetSessionExercise | null, labels: ReadonlyMap<number, string>): SheetExercise["clips"] {
  if (e === null) return [];
  const words = (n: number | null) => {
    const l = n === null ? undefined : labels.get(n);
    return l === undefined ? "Clip" : l.startsWith("W") ? `Warm-up ${l.slice(1)}` : `Set ${l}`;
  };
  return [...e.clips]
    .sort((a, b) => (a.set_number ?? 0) - (b.set_number ?? 0) || (a.recorded_at ?? "").localeCompare(b.recorded_at ?? "") || (a.id < b.id ? -1 : 1))
    .map((c) => ({ id: c.id, storagePath: c.storage_path, mediaKind: c.media_kind, recordedAtISO: c.recorded_at, setNumber: c.set_number, label: words(c.set_number) }));
}

/** One PAIRED exercise: every written set beside the log at its place (set N ↔ `set_number` N), then any extra logs. */
const targetOf = (e: SheetSessionExercise | null): SheetExercise["commentTarget"] =>
  e === null || e.coachExerciseId == null ? null : { exerciseRowId: e.id, coachExerciseId: e.coachExerciseId };

function pairedExercise(id: string, name: string, rule: SheetExercise["rule"], sets: readonly PlanSet[], performed: SheetSessionExercise | null,
                        planRowId: string | null, libraryId: string | null, km: boolean, untilLost = false): SheetExercise {
  const logs = logsByNumber(performed?.logs ?? []);
  const rows: SheetSetRow[] = [];
  const labels = new Map<number, string>();
  let ramp = 0, work = 0;
  sortedSets(sets).forEach((s, i) => {
    const log = logs.get(i + 1);
    const written = setWords(s.reps, s.time_seconds, s.weight_kg, s.distance_m ?? null, s.rounds ?? null, km);
    labels.set(i + 1, isRampSet(s) ? `W${String(ramp + 1)}` : String(work + 1));
    if (isRampSet(s)) {
      ramp += 1;
      rows.push({ label: `W${String(ramp)}`, written, done: log === undefined || !isPerformed(log.status) ? NOT_LOGGED : logWords(log, km), rpe: rpeText(log?.rpe ?? null), state: "ramp", note: noteOf(log), typed: false });
      return;
    }
    work += 1;
    const state = classify(s, log);
    const typedRow = isTyped(s) || (log?.distance_m ?? null) !== null || (log?.rounds ?? null) !== null;
    const done = state === "notLogged" ? NOT_LOGGED : state === "skipped" ? "skipped"
      : typedRow ? (log === undefined ? NOT_LOGGED : logWords(log, km, isTyped(s)))
      // A balance row's sets are holds by KIND (`untilLost`): the logged seconds are the record whatever the written set carries.
      : setWords(s.time_seconds !== null || untilLost ? null : log?.reps_completed ?? null, s.time_seconds !== null || untilLost ? log?.hold_seconds ?? null : null, log?.weight_kg ?? null);
    rows.push({ label: String(work), written, done, rpe: state === "notLogged" || state === "skipped" ? "" : rpeText(log?.rpe ?? null), state, note: noteOf(log), typed: typedRow });
  });
  // A set logged beyond what was written is work too — more than asked, drawn as beaten with nothing written beside it.
  for (const [n, log] of [...logs].sort((a, b) => a[0] - b[0])) {
    if (n <= sets.length || !isWorking(log.status)) continue;
    work += 1;
    labels.set(n, String(work));
    rows.push({ label: String(work), written: NOT_LOGGED, done: logWords(log, km), rpe: rpeText(log.rpe), state: "beaten", note: noteOf(log), typed: typedLog(log) });
  }
  return { id, name, planRowId, libraryId, commentTarget: targetOf(performed), rule, sets: rows, clips: clipsOf(performed, labels) };
}

/** One exercise read off the session alone (no plan to set it beside): what was done, in ink, with nothing claimed. */
function unpairedExercise(e: SheetSessionExercise, name: string, rule: SheetExercise["rule"], km: boolean): SheetExercise {
  const rows: SheetSetRow[] = [];
  const labels = new Map<number, string>();
  let ramp = 0, work = 0;
  for (const log of [...e.logs].sort((a, b) => (a.set_number ?? 0) - (b.set_number ?? 0))) {
    const done = logWords(log, km);
    if (log.set_number !== null) labels.set(log.set_number, log.status === "warmup" ? `W${String(ramp + 1)}` : String(work + 1));
    if (log.status === "warmup") { ramp += 1; rows.push({ label: `W${String(ramp)}`, written: NOT_LOGGED, done, rpe: rpeText(log.rpe), state: "ramp", note: noteOf(log), typed: false }); continue; }
    work += 1;
    const state: SetState = log.status === "skipped" ? "skipped" : isWorking(log.status) ? "equal" : "notLogged";
    rows.push({ label: String(work), written: NOT_LOGGED, done: state === "skipped" ? "skipped" : state === "notLogged" ? NOT_LOGGED : done, rpe: rpeText(log.rpe), state, note: noteOf(log), typed: typedLog(log) });
  }
  return { id: e.id, name, planRowId: null, libraryId: e.key, commentTarget: targetOf(e), rule, sets: rows, clips: clipsOf(e, labels) };
}

export function reviewSheet(input: {
  /** The queue's row for this workout — the chosen record, and the head's numbers. */
  readonly row: QueueRow;
  /** Every record the page read for this client's day; only `row.sessionId` is read. */
  readonly sessions: readonly SheetSession[];
  readonly plan: SheetPlan | null;
  /** The movement's name by library id. */
  readonly names: ReadonlyMap<string, string>;
  /** The library row's `progression_config` by library id. */
  readonly rules: ReadonlyMap<string, unknown>;
  /** W136 B2 S3 — the library ids whose field is `distance_km` (`coach_exercises.tracking_fields`); others read in metres. */
  readonly kmKeys?: ReadonlySet<string>;
}): ReviewSheet | null {
  const { row } = input;
  const session = input.sessions.find((s) => s.id === row.sessionId);
  if (session === undefined) return null;
  const day = session.scheduledDate?.slice(0, 10) ?? null;
  const otherRecords = input.sessions.filter((s) => s.id !== session.id && s.userId === session.userId && s.kind === session.kind
    && (s.scheduledDate?.slice(0, 10) ?? null) === day).length;
  const nameOf = (key: string | null) => (key === null ? "Unnamed exercise" : (input.names.get(key) ?? "Unnamed exercise"));
  const ruleOf = (key: string | null) => (key === null ? null : ruleTag(input.rules.get(key) ?? null));
  const performed = [...session.exercises].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const kmOf = (key: string | null) => key !== null && (input.kmKeys?.has(key) ?? false);

  let bands: SheetBand[] = [];
  let unpaired: string | null = null;
  const flat = input.plan === null ? [] : flatPlan(input.plan.sections);
  // THE ONE PAIRING RULE (`setVerdict.pairWithPlan` → `training.dayView`): place by place, the same library id at each.
  const paired = input.plan === null ? null : pairWithPlan(flat, performed);
  if (input.plan !== null && paired !== null) {
    const bySection = new Map<number, { section: PlanSection; index: number; exercises: { plan: PlanExercise; performed: SheetSessionExercise | null }[] }>();
    flat.forEach((f, i) => {
      const perf = paired[i] ?? null;
      const entry = bySection.get(f.sectionIndex) ?? { section: f.section, index: f.sectionIndex, exercises: [] };
      entry.exercises.push({ plan: f.exercise, performed: perf });
      bySection.set(f.sectionIndex, entry);
    });
    bands = [...bySection.values()].sort((a, b) => a.index - b.index).map((sec): SheetBand => {
      // W104 — the coach's name or NO heading: an unnamed section draws its exercises bare, never `Part N` / `MAIN`.
      const title = sectionName(sec.section.name);
      if (isFoldedSection(sec.section.section_type)) {
        const written = sec.exercises.reduce((n, x) => n + x.plan.sets.length, 0);
        const logged = sec.exercises.reduce((n, x) => n + (x.performed?.logs ?? []).filter((l) => isPerformed(l.status)).length, 0);
        return { kind: "folded", line: foldedLine(title, sec.exercises.length, written, logged) };
      }
      const built = sec.exercises.map((x) => pairedExercise(x.performed?.id ?? `${sec.index}:${String(x.plan.position)}`, nameOf(x.plan.exercise_id), ruleOf(x.plan.exercise_id), x.plan.sets, x.performed, x.plan.id ?? null, x.plan.exercise_id, kmOf(x.plan.exercise_id), x.plan.hold_until_lost === true));
      const runs = supersetRuns(sec.exercises.map((x) => ({ supersetWithNext: x.plan.superset_with_next === true })));
      const items: SheetItem[] = runs.map((run) => run.length === 1
        ? { kind: "single", exercise: built[run[0] as number] as SheetExercise }
        : { kind: "superset", exercises: run.map((i) => built[i] as SheetExercise) });
      return { kind: "section", title, items };
    });
  } else {
    unpaired = input.plan === null
      ? "No single workout was assigned for this day, so the sets are shown as logged, with nothing written beside them."
      : "This record does not line up with the workout you assigned, so the sets are shown as logged.";
    const main = performed.filter((e) => e.section_role === null || !isFoldedSection(e.section_role));
    bands = main.length === 0 ? [] : [{ kind: "section", title: input.plan?.workoutName ?? row.workout ?? "Workout",
      items: main.map((e) => ({ kind: "single", exercise: unpairedExercise(e, nameOf(e.key), ruleOf(e.key), kmOf(e.key)) })) }];
  }

  const tally = { done: 0, short: 0, skipped: 0, notLogged: 0, ramps: 0 };
  let rpeLogged = false;
  for (const b of bands) {
    if (b.kind !== "section") continue;
    for (const it of b.items) {
      for (const ex of it.kind === "single" ? [it.exercise] : it.exercises) {
        for (const s of ex.sets) {
          if (s.rpe !== "") rpeLogged = true;
          if (s.state === "short") tally.short += 1;
          if (s.state === "equal" || s.state === "short" || s.state === "beaten") tally.done += 1;
          else if (s.state === "skipped") tally.skipped += 1;
          else if (s.state === "notLogged") tally.notLogged += 1;
          else tally.ramps += 1;
        }
      }
    }
  }

  const note = session.note !== null && session.note.trim() !== "" ? session.note.trim() : null;
  const workout = row.workout ?? input.plan?.workoutName ?? null;
  const finished = finishedWords(row.finish);
  const minutes = session.minutes !== null && session.minutes > 0 ? `${String(session.minutes)} min` : null;
  return {
    head: {
      client: row.name,
      workout,
      finished,
      minutes,
      line: [workout, finished, minutes].filter((x): x is string => x !== null).join(" · "),
      chips: [completionWords(row) ?? setsChip(row.done, row.written), ...row.flags.map(flagWords)],
    },
    counts: { done: row.done, written: row.written, skipped: row.skipped, notLogged: row.notLogged },
    tally,
    rpeLogged,
    bands,
    unpaired,
    workoutNote: note,
    otherRecords,
    otherRecordsLine: otherRecordsWords(otherRecords),
  };
}

/* ── the queue position ─────────────────────────────────────────────────────────────────────────── */

export type QueuePosition = { readonly index: number; readonly total: number; readonly prev: string | null; readonly next: string | null };

/** Where this workout stands in the queue's own order (`ReviewQueue.waiting`, oldest finish first); null when it waits
 *  in no queue (reviewed, or out of the window) — the page then says only `‹ Review queue`. */
export function queuePosition(rows: readonly { sessionId: string }[], id: string): QueuePosition | null {
  const i = rows.findIndex((r) => r.sessionId === id);
  if (i < 0) return null;
  return { index: i + 1, total: rows.length, prev: rows[i - 1]?.sessionId ?? null, next: rows[i + 1]?.sessionId ?? null };
}

/** `‹ Review queue · 2 of 6`; `‹ Review queue` when the workout waits in no queue. */
export function positionLine(p: QueuePosition | null): string {
  return p === null ? "‹ Review queue" : `‹ Review queue · ${String(p.index)} of ${String(p.total)}`;
}

/** Where `Skip` goes: the next in the queue, or back to the queue when this was the last (null). */
export function skipTarget(p: QueuePosition | null): string | null {
  return p?.next ?? null;
}

/** ← and → walk the queue; nothing when a field has the keyboard, or with a modifier held (the browser's own). */
export function arrowTarget(p: QueuePosition | null, key: string, typing: boolean, modified: boolean): string | null {
  if (p === null || typing || modified) return null;
  if (key === "ArrowLeft") return p.prev;
  if (key === "ArrowRight") return p.next;
  return null;
}

/* ── W93: a clip in its set's row ──────────────────────────────────────────────────────────────── */

/** The table row a clip belongs to, read back from the clip's own label (`clipsOf` writes it from the SAME numbering the
 *  table uses): `Set 2` → `2`, `Warm-up 1` → `W1`. A clip with no set (`Clip`) belongs to no row — it stays under the
 *  table, so nothing the client filmed is ever hidden because it cannot be placed. */
export function clipRowLabel(label: string): string | null {
  const set = /^Set (\d+)$/.exec(label);
  if (set !== null) return set[1] ?? null;
  const ramp = /^Warm-up (\d+)$/.exec(label);
  return ramp !== null ? `W${ramp[1] ?? ""}` : null;
}

/** The clips of one row, and the clips no row can hold — together, every clip exactly once. */
export function clipsByRow(clips: SheetExercise["clips"]): { byRow: ReadonlyMap<string, SheetExercise["clips"]>; loose: SheetExercise["clips"] } {
  const byRow = new Map<string, SheetExercise["clips"][number][]>();
  const loose: SheetExercise["clips"][number][] = [];
  for (const c of clips) {
    const row = clipRowLabel(c.label);
    if (row === null) { loose.push(c); continue; }
    byRow.set(row, [...(byRow.get(row) ?? []), c]);
  }
  return { byRow, loose };
}

/** The Done cell's class by the set's state — ONE table for the review page and the Dashboard's overview window (W93). */
export const DONE_CLASS: Record<SheetSetRow["state"], string> = {
  equal: "review-done", short: "review-done is-short", beaten: "review-done is-beaten",
  notLogged: "review-done is-absent", skipped: "review-done is-absent", ramp: "review-done",
};
